import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import {
  OrganizationMember,
  OrganizationRole,
  isRoleEqual,
} from '../../services/organizationApi';

interface UpdateMemberRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  member: OrganizationMember | null;
  newRole: OrganizationRole | string | number;
  setNewRole: (role: OrganizationRole | string | number) => void;
  loading: boolean;
}

const UpdateMemberRoleDialog: React.FC<UpdateMemberRoleDialogProps> = ({
  open,
  onClose,
  onSubmit,
  member,
  newRole,
  setNewRole,
  loading,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  if (!member) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Update Member Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Update role for <strong>{member.user.username}</strong> ({member.user.email})
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Changing a member's role will affect their permissions within the organization.
            </Alert>

            <FormControl fullWidth margin="normal">
              <InputLabel>New Role</InputLabel>
              <Select
                value={newRole}
                label="New Role"
                onChange={(e) => setNewRole(e.target.value as OrganizationRole)}
                disabled={loading}
              >
                <MenuItem value={OrganizationRole.VIEWER}>Viewer (Read-only access)</MenuItem>
                <MenuItem value={OrganizationRole.MEMBER}>Member (Standard access)</MenuItem>
                <MenuItem value={OrganizationRole.ADMIN}>Admin (Manage members & invitations)</MenuItem>
                <MenuItem value={OrganizationRole.OWNER}>Owner (Full control)</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Current role:</strong> {isRoleEqual(member.role, OrganizationRole.OWNER) ? 'Owner' : 
                isRoleEqual(member.role, OrganizationRole.ADMIN) ? 'Admin' : 
                isRoleEqual(member.role, OrganizationRole.MEMBER) ? 'Member' : 'Viewer'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || newRole === member.role}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Updating...' : 'Update Role'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateMemberRoleDialog;
